import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';

import {
  GET_ALERT_NOTIFICATION_SETTINGS,
  UPDATE_ALERT_NOTIFICATION_SETTINGS,
} from '../../../http/apollo-queries';
import { ToggleButton, Button } from '../../UI';
import { useAuth } from '../../../context/Auth';
import { toast } from 'react-toastify';
import { logger } from '../../../helpers/logger';
import { INotificationSettings } from '../../../types/model';

const NotificationSettings: React.FC = () => {
  // Hooks
  const { t } = useTranslation();
  const { user } = useAuth();

  // Queries
  const { data: { getAlertNotificationSettings } = {}, loading } = useQuery<{
    getAlertNotificationSettings: INotificationSettings;
  }>(GET_ALERT_NOTIFICATION_SETTINGS, {
    variables: {
      ownerId: user!.id,
    },
  });

  const [updateSettings, { loading: loadingUpdate }] = useMutation<{
    updateAlertNotificationSettings: INotificationSettings;
  }>(UPDATE_ALERT_NOTIFICATION_SETTINGS, {
    refetchQueries: [
      {
        query: GET_ALERT_NOTIFICATION_SETTINGS,
        variables: { ownerId: user!.id },
      },
    ],
    awaitRefetchQueries: true,
    onError(error) {
      toast.error(t('alerts.settingsModal.error'));
      logger.error('Notifications', error);
    },
    update(cache, { data }) {
      const response = data?.updateAlertNotificationSettings;

      cache.writeQuery<{
        getAlertNotificationSettings: INotificationSettings | undefined;
      }>({
        query: GET_ALERT_NOTIFICATION_SETTINGS,
        data: { getAlertNotificationSettings: response },
      });
    },
    onCompleted(data) {
      setState(data.updateAlertNotificationSettings);
    },
  });

  // State
  const [state, setState] = useState(getAlertNotificationSettings);

  useEffect(() => {
    setState(getAlertNotificationSettings);
  }, [getAlertNotificationSettings]);

  return (
    <div className="mx-auto text-center">
      <div className="flex flex-col justify-between py-4">
        {Object.entries(state ?? {})
          .filter(
            ([setting]) => setting !== '__typename' && setting !== 'owner'
          )
          .map(([setting, choice]) => {
            return (
              <div
                key={setting}
                className="flex justify-between py-4 border-b-1 border-grey-10 dark:border-grey-80 mt-4"
              >
                <span className="pr-36">
                  {t(`alerts.settingsModal.${setting}`)}
                </span>
                <ToggleButton
                  activatedLabel="On"
                  deactivatedLabel="Off"
                  renderLabelOn="left"
                  isActivated={choice}
                  isDisabled={loadingUpdate}
                  onActivate={() =>
                    setState(
                      (prev) =>
                        ({ ...prev, [setting]: true } as INotificationSettings)
                    )
                  }
                  onDeactivate={() =>
                    setState(
                      (prev) =>
                        ({ ...prev, [setting]: false } as INotificationSettings)
                    )
                  }
                />
              </div>
            );
          })}
      </div>
      <div className="flex justify-center pt-8 mb-16">
        <Button
          type="button"
          variant="primary"
          size="small"
          onClick={() => {
            return updateSettings({
              variables: state,
            });
          }}
          loading={loading || loadingUpdate}
        >
          {t('alerts.save')}
        </Button>
      </div>
    </div>
  );
};

export default NotificationSettings;
